import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { paths } from '../__generated__/openapi-v4';
import apiClientV4 from './client';

/**
 * Một lần gọi v4 thất bại, kèm đủ thứ để chỗ gọi phân biệt được lỗi nào.
 *
 * `message`/`detail` là ghi chú cho dev ("invalid input: not enough stones"), không phải câu
 * hiển thị cho học sinh: chỗ gọi tự map `status` sang copy của mình. Mỗi endpoint tự mô tả
 * status của nó (vd đổi quà: 409 hết hàng/quá hạn mức, 422 không đủ đá, 404 không thuộc lớp).
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly title?: string;
  readonly detail?: string;

  constructor(message: string, parts: { status?: number; title?: string; detail?: string }) {
    super(message);
    this.name = 'ApiError';
    this.status = parts.status;
    this.title = parts.title;
    this.detail = parts.detail;
  }
}

/**
 * Lấy câu mô tả từ body ErrorModel.
 *
 * API trả lỗi dạng { title, status, detail, errors? }; `detail` là phần nói rõ lý do.
 * JSON.stringify cả object là cách cuối, chỉ dùng khi không đọc được field nào.
 */
function errorMessage(error: unknown): string {
  if (typeof error === 'string') return error;

  if (error && typeof error === 'object') {
    const body = error as { detail?: unknown; title?: unknown; errors?: unknown };
    const fieldErrors = Array.isArray(body.errors)
      ? body.errors
          .map((item) => {
            const entry = item as { location?: unknown; message?: unknown };
            return [entry.location, entry.message].filter(Boolean).join(' ');
          })
          .filter(Boolean)
          .join('; ')
      : '';
    const main = [body.detail, body.title].find((value) => typeof value === 'string');

    if (main) return fieldErrors ? `${main as string} (${fieldErrors})` : (main as string);
    if (fieldErrors) return fieldErrors;
  }

  return JSON.stringify(error);
}

/** Body ErrorModel → ApiError. `responseStatus` dùng khi body không nhắc lại status. */
function toApiError(error: unknown, responseStatus?: number): ApiError {
  const body =
    error && typeof error === 'object'
      ? (error as { status?: unknown; title?: unknown; detail?: unknown })
      : null;

  return new ApiError(errorMessage(error), {
    status: typeof body?.status === 'number' ? body.status : responseStatus,
    title: typeof body?.title === 'string' ? body.title : undefined,
    detail: typeof body?.detail === 'string' ? body.detail : undefined,
  });
}

// ============================================
// Type helpers: bóc request/response ra khỏi `paths`
// ============================================
type PathMethod<P extends keyof paths, M extends keyof paths[P]> = paths[P][M];

type RequestBody<P extends keyof paths, M extends keyof paths[P]> =
  PathMethod<P, M> extends { requestBody: { content: { 'application/json': infer B } } }
    ? B
    : never;

type ResponseData<P extends keyof paths, M extends keyof paths[P]> =
  PathMethod<P, M> extends { responses: { 200: { content: { 'application/json': infer D } } } }
    ? D
    : PathMethod<P, M> extends { responses: { 201: { content: { 'application/json': infer D } } } }
      ? D
      : PathMethod<P, M> extends { responses: { 204: { content?: never } } }
        ? void
        : unknown;

type PathParams<P extends keyof paths, M extends keyof paths[P]> =
  PathMethod<P, M> extends { parameters: { path?: infer Params } } ? Params : never;

type QueryParams<P extends keyof paths, M extends keyof paths[P]> =
  PathMethod<P, M> extends { parameters: { query?: infer Params } } ? Params : never;

type ApiParams<P extends keyof paths, M extends keyof paths[P]> =
  PathParams<P, M> extends never
    ? QueryParams<P, M> extends never
      ? undefined
      : { query?: QueryParams<P, M> }
    : QueryParams<P, M> extends never
      ? { path: PathParams<P, M> }
      : PathParams<P, M> extends undefined
        ? { query?: QueryParams<P, M> }
        : { path: PathParams<P, M>; query?: QueryParams<P, M> };

// ============================================
// GET
// ============================================
export function useApiQuery<P extends keyof paths, M extends keyof paths[P] = 'get'>(
  path: P,
  params?: ApiParams<P, M>,
  options?: Omit<UseQueryOptions<ResponseData<P, M>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ResponseData<P, M>>({
    queryKey: [path, params],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error, response } = await (apiClientV4.GET as any)(path, { params });

      if (error) {
        throw toApiError(error, response?.status);
      }

      return data as ResponseData<P, M>;
    },
    ...options,
  });
}

// ============================================
// POST / PUT / PATCH / DELETE
// ============================================
export function useApiMutation<
  P extends keyof paths,
  M extends 'post' | 'put' | 'patch' | 'delete',
>(
  method: M,
  path: P,
  options?: Omit<
    UseMutationOptions<
      ResponseData<P, M>,
      Error,
      { body?: RequestBody<P, M>; path?: PathParams<P, M>; query?: QueryParams<P, M> }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    ResponseData<P, M>,
    Error,
    { body?: RequestBody<P, M>; path?: PathParams<P, M>; query?: QueryParams<P, M> }
  >({
    mutationFn: async ({ body, path: pathParams, query }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiMethod = apiClientV4[method.toUpperCase() as Uppercase<M>] as any;

      const { data, error, response } = await apiMethod(path, {
        body,
        params: { path: pathParams, query },
      });

      if (error) {
        throw toApiError(error, response?.status);
      }

      return data as ResponseData<P, M>;
    },
    onSuccess: () => {
      // Mọi query của cùng endpoint đó coi như cũ sau khi ghi thành công.
      queryClient.invalidateQueries({ queryKey: [path] });
    },
    ...options,
  });
}

export const useApiPost = <P extends keyof paths>(
  path: P,
  options?: Parameters<typeof useApiMutation<P, 'post'>>[2],
) => useApiMutation('post', path, options);

export const useApiPut = <P extends keyof paths>(
  path: P,
  options?: Parameters<typeof useApiMutation<P, 'put'>>[2],
) => useApiMutation('put', path, options);

export const useApiPatch = <P extends keyof paths>(
  path: P,
  options?: Parameters<typeof useApiMutation<P, 'patch'>>[2],
) => useApiMutation('patch', path, options);

export const useApiDelete = <P extends keyof paths>(
  path: P,
  options?: Parameters<typeof useApiMutation<P, 'delete'>>[2],
) => useApiMutation('delete', path, options);
